/**
 * EvidenceBadge.jsx — iter655
 * Shows a confidence pill (🟢/🟡/🟠) next to a BHI score with an "📚 Source"
 * popover that loads citations from GET /api/bhi/evidence/{metric_id}.
 *
 * Strict educational tone — never claims diagnostic value.
 */
import React, { useState } from "react";
import axios from "axios";
import { BookOpen, Info, X } from "lucide-react";
import { useAuth } from "@/App";

const BADGE = {
  high:      { bg: "bg-emerald-100 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-300", dot: "🟢", label: { bg: "Висока", en: "High", es: "Alta" } },
  mid:       { bg: "bg-amber-100 dark:bg-amber-500/20",     text: "text-amber-700 dark:text-amber-300",     dot: "🟡", label: { bg: "Средна", en: "Mid",  es: "Media" } },
  // iter656 — Internal-only trust level. NEVER shown as "Heuristic" in patient-facing UI;
  // collapsed to the 🟡 Mid presentation (educational tone preserved). Internal audit
  // data (registry + tests) still differentiates.
  heuristic: { bg: "bg-amber-100 dark:bg-amber-500/20",     text: "text-amber-700 dark:text-amber-300",     dot: "🟡", label: { bg: "Средна", en: "Mid",  es: "Media" } },
};

const TX = {
  bg: { source: "📚 Източник", trust: "Trust", citations: "Цитати", close: "Затвори", confidence: "Сигурност на скора" },
  en: { source: "📚 Source",    trust: "Trust", citations: "Citations", close: "Close", confidence: "Score confidence" },
  es: { source: "📚 Fuente",    trust: "Trust", citations: "Citas", close: "Cerrar", confidence: "Confianza del puntaje" },
};

export default function EvidenceBadge({ confidence = "mid", evidenceId, lang = "bg", showLabel = true }) {
  const { API } = useAuth();
  const t = TX[lang] || TX.bg;
  const b = BADGE[confidence] || BADGE.mid;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const openSource = async () => {
    if (!evidenceId) return;
    setOpen(true);
    if (data || loading) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/bhi/evidence/${evidenceId}`);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${b.bg} ${b.text}`}
        title={t.confidence}
        data-testid={`evidence-badge-${confidence}`}
      >
        <span>{b.dot}</span>
        {showLabel && <span>{b.label[lang] || b.label.bg}</span>}
      </span>

      {evidenceId && (
        <button
          onClick={openSource}
          className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
          data-testid={`evidence-source-btn-${evidenceId}`}
        >
          <BookOpen className="w-3 h-3" /> {t.source}
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="evidence-modal"
        >
          <div
            className="bg-white dark:bg-[hsl(222,47%,11%)] rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto p-5 shadow-2xl border border-gray-200 dark:border-white/10 m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" />
                {data?.label?.[lang] || evidenceId}
              </h3>
              <button onClick={() => setOpen(false)} className="opacity-60 hover:opacity-100" data-testid="evidence-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>

            {loading && <div className="text-sm opacity-60">Loading…</div>}
            {err && <div className="text-sm text-red-500">{err}</div>}
            {data && (
              <div className="space-y-3">
                {(data.thresholds || []).map((thr, i) => {
                  const tb = BADGE[thr.trust] || BADGE.mid;
                  return (
                    <div key={i} className="border-l-4 border-gray-200 dark:border-white/10 pl-3 py-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${tb.bg} ${tb.text}`}>
                          {tb.dot} {tb.label[lang] || tb.label.bg}
                        </span>
                      </div>
                      <div className="text-sm font-semibold mb-1">{thr.rule}</div>
                      {thr.note && <div className="text-xs opacity-60 italic mb-1">{thr.note}</div>}
                      {(thr.citations || []).length > 0 && (
                        <ul className="space-y-1 mt-1">
                          {thr.citations.map((c, j) => (
                            <li key={j} className="text-[11px] opacity-90">
                              {c.title}
                              {c.authors && <span className="opacity-60"> — {c.authors}</span>}
                              {c.journal && <span className="opacity-60 italic">, {c.journal}</span>}
                              {c.year && <span className="opacity-60">, {c.year}</span>}
                              {c.pmid && (
                                <a
                                  href={`https://pubmed.ncbi.nlm.nih.gov/${c.pmid}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="ml-1 text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  PMID: {c.pmid}
                                </a>
                              )}
                              {c.url && (
                                <a
                                  href={c.url} target="_blank" rel="noopener noreferrer"
                                  className="ml-1 text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  ↗
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
